export const OralHygieneListTypes = {
  HygieneTrend: 'hygieneTrend',
  HygieneScore: 'hygieneScore',
  CavityRisk: 'cavityRisk',
  PerioRisk: 'perioRisk'
}

/**
 * The report widgets. Each key doubles as the i18n key (pages.oralHygieneReport.widgets.<key>)
 * and as the widgetCounts key of useOralHygieneReport (not the aggregation response key).
 */
export const OralHygieneWidgets = {
  HygieneTrend: 'hygieneTrend',
  HighCavityRisk: 'highCavityRisk',
  HighPerioRisk: 'highPerioRisk',
  HygienePayAttention: 'hygienePayAttention'
}

export const HygieneTrends = {
  Improving: 'improving',
  Stable: 'stable',
  Receding: 'receding',
  Inconsistent: 'inconsistent'
}

/**
 * The PatientSearchModel field that each patient view filters and categorizes by.
 */
export const OralHygieneSearchFieldsByListType = {
  [OralHygieneListTypes.HygieneTrend]: 'hygieneTrend',
  [OralHygieneListTypes.HygieneScore]: 'lastHygieneScore',
  [OralHygieneListTypes.CavityRisk]: 'lastDentalRisk',
  [OralHygieneListTypes.PerioRisk]: 'lastPerioRisk'
}

/**
 * The existence clause each patient view applies to its search field. AppSync's
 * SearchableIntFilterInput has no `exists` member, so the Int-backed views assert
 * existence by matching their positive model values instead.
 */
export const OralHygieneSearchFieldExistenceFilterByListType = {
  [OralHygieneListTypes.HygieneTrend]: { exists: true },
  [OralHygieneListTypes.HygieneScore]: { gt: 0 },
  [OralHygieneListTypes.CavityRisk]: { gt: 0 },
  [OralHygieneListTypes.PerioRisk]: { gt: 0 }
}

/**
 * The category chips of each patient view. `key` doubles as the i18n label key
 * (pages.oralHygieneReport.patientsList.categories.<key>) and as the aggregation response key,
 * while `searchValue` is the raw model value stored in the patient search model.
 */
export const OralHygieneCategoriesByListType = {
  [OralHygieneListTypes.HygieneTrend]: [
    { key: HygieneTrends.Improving, searchValue: HygieneTrends.Improving },
    { key: HygieneTrends.Stable, searchValue: HygieneTrends.Stable },
    { key: HygieneTrends.Receding, searchValue: HygieneTrends.Receding },
    { key: HygieneTrends.Inconsistent, searchValue: HygieneTrends.Inconsistent }
  ],
  [OralHygieneListTypes.HygieneScore]: [
    { key: 'optimal', searchValue: 1 },
    { key: 'good', searchValue: 2 },
    { key: 'fair', searchValue: 3 },
    { key: 'payAttention', searchValue: 4 }
  ],
  [OralHygieneListTypes.CavityRisk]: [
    { key: 'low', searchValue: 1 },
    { key: 'medium', searchValue: 2 },
    { key: 'high', searchValue: 3 }
  ],
  [OralHygieneListTypes.PerioRisk]: [
    { key: 'low', searchValue: 1 },
    { key: 'medium', searchValue: 2 },
    { key: 'high', searchValue: 3 }
  ]
}

/**
 * The aggregation endpoint distribution that backs each patient view.
 */
export const OralHygieneAggregationKeysByListType = {
  [OralHygieneListTypes.HygieneTrend]: 'hygieneTrend',
  [OralHygieneListTypes.HygieneScore]: 'hygieneScore',
  [OralHygieneListTypes.CavityRisk]: 'dentalRisk',
  [OralHygieneListTypes.PerioRisk]: 'perioRisk'
}

export const HygieneScoreCategoryKeysByValue = {
  1: 'optimal',
  2: 'good',
  3: 'fair',
  4: 'payAttention'
}

export const RiskCategoryKeysByValue = {
  1: 'low',
  2: 'medium',
  3: 'high'
}

/**
 * Figma frame 218 color scale shared by the trend chart bars, the chip legend squares and the
 * list row values. Every category resolves to one of these tones (plus neutral). `Mixed` is the
 * Inconsistent trend, which sits outside the good-to-bad scale. Its violet is a placeholder until
 * design provides one, chosen to carry no other meaning in the app (the greys read as inactive).
 */
export const OralHygieneTones = {
  Good: 'good',
  Warning: 'warning',
  Bad: 'bad',
  Mixed: 'mixed',
  Neutral: 'neutral'
}

export const OralHygieneToneColors = {
  [OralHygieneTones.Good]: '#3DBE7A',
  [OralHygieneTones.Warning]: '#F2B63A',
  [OralHygieneTones.Bad]: '#EB5757',
  [OralHygieneTones.Mixed]: '#7C5CDB',
  [OralHygieneTones.Neutral]: '#001335'
}

export const OralHygieneCategoryTones = {
  [HygieneTrends.Improving]: OralHygieneTones.Good,
  [HygieneTrends.Stable]: OralHygieneTones.Warning,
  [HygieneTrends.Receding]: OralHygieneTones.Bad,
  [HygieneTrends.Inconsistent]: OralHygieneTones.Mixed,
  optimal: OralHygieneTones.Good,
  good: OralHygieneTones.Good,
  fair: OralHygieneTones.Warning,
  payAttention: OralHygieneTones.Bad,
  low: OralHygieneTones.Good,
  medium: OralHygieneTones.Warning,
  high: OralHygieneTones.Bad
}

export const getOralHygieneCategoryColor = categoryKey =>
  OralHygieneToneColors[OralHygieneCategoryTones[categoryKey] || OralHygieneTones.Neutral]
